import { useTranslation } from "react-i18next";
import { Icon, type IconName } from "../../design-system/primitives/Icon";
import { useBannerStore } from "../../store/bannerStore";

/** Programa Axon Empowering IEC 60870-5-104 (edicion 2026). Mismo destino
 *  que el banner del proyecto de referencia. */
const AXON_EMPOWERING_IEC104_URL =
  "https://empowering.axongroup.com.co/products/Programa%20IEC%2060870-5-104%E2%80%93%20%20Versi%C3%B3n%201.0%20(2026)";

/**
 * Franja promocional renderizada entre la barra superior del workspace y
 * el contenido de la pagina. Promociona el programa Axon Empowering IEC
 * 60870-5-104, el curso en vivo dirigido a ingenieros del sector.
 *
 * La visibilidad es por sesion de trabajo: el boton de descartar
 * (`collapse`) oculta el banner solo para la sesion actual; entrar de
 * nuevo al workspace lo restaura (ver `bannerStore`). El icono es distinto
 * de la X de cierre de la cabecera para que las dos acciones no se lean
 * como lo mismo.
 *
 * Distribucion -- de izquierda a derecha en una sola franja de 88 px:
 *   1. Bloque de marca: birrete + logotipo apilado "AXON / EMPOWERING".
 *   2. Titulo (con el codigo del protocolo resaltado) + lema en una linea.
 *   3. Tres celdas de beneficio: fecha de inicio, modalidad, certificacion.
 *      La fila de beneficios se colapsa en pantallas menores a `xl`.
 *   4. Boton CTA + control para descartar.
 */
export function WorkspaceBanner() {
  const { t } = useTranslation("banner");
  const visible = useBannerStore((s) => s.visible);
  const dismiss = useBannerStore((s) => s.dismiss);

  if (!visible) return null;

  return (
    <div
      className="flex h-[88px] flex-shrink-0 items-stretch border-b border-[var(--color-border)] bg-[var(--color-empowering-soft)]"
      role="region"
      aria-label={t("aria")}
    >
      {/* Bloque de marca */}
      <div className="flex flex-shrink-0 items-center gap-2 px-3">
        <Icon
          name="school"
          size={28}
          className="text-[var(--color-empowering)]"
        />
        <div className="flex flex-col leading-tight">
          <span className="text-[11px] font-bold tracking-[0.18em] text-[var(--color-on-surface)]">
            {t("brand.line1")}
          </span>
          <span className="text-[11px] font-bold tracking-[0.18em] text-[var(--color-empowering)]">
            {t("brand.line2")}
          </span>
        </div>
      </div>

      <span
        className="my-3 w-px flex-shrink-0 bg-[var(--color-outline-variant)]"
        aria-hidden
      />

      {/* Titulo + lema */}
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 px-3">
        <h2 className="truncate text-[15px] font-bold leading-tight text-[var(--color-on-surface)]">
          {t("title")}
        </h2>
        <p className="truncate text-[12px] leading-tight text-[var(--color-on-surface-variant)]">
          {t("tagline")}
        </p>
      </div>

      {/* Fila de beneficios: oculta bajo xl para no saturar la barra. */}
      <div className="hidden flex-shrink-0 items-center gap-4 px-3 xl:flex">
        <BannerFeature icon="event" label={t("features.start.label")} value={t("features.start.value")} />
        <BannerFeature icon="laptop" label={t("features.mode.label")} value={t("features.mode.value")} />
        <BannerFeature
          icon="premium"
          label={t("features.certification.label")}
          value={t("features.certification.value")}
        />
      </div>

      {/* CTA + descartar */}
      <div className="flex flex-shrink-0 items-center gap-1.5 px-3">
        <a
          href={AXON_EMPOWERING_IEC104_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("cta.aria")}
          className="inline-flex h-8 items-center gap-1.5 rounded bg-[var(--color-empowering)] px-3 text-[12px] font-bold uppercase tracking-wider text-white shadow-sm transition-opacity hover:opacity-90 focus:outline-none"
        >
          <span>{t("cta.label")}</span>
          <Icon name="arrow_forward" size={14} />
        </a>
        <button
          type="button"
          onClick={dismiss}
          className="ml-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-[var(--color-on-surface-variant)] transition-colors hover:bg-[var(--color-surface-variant)] hover:text-[var(--color-on-surface)] focus:outline-none"
          title={t("close.title")}
          aria-label={t("close.aria")}
        >
          <Icon name="collapse" size={18} />
        </button>
      </div>
    </div>
  );
}

interface BannerFeatureProps {
  icon: IconName;
  label: string;
  value: string;
}

/**
 * Celda icono + etiqueta + valor de la fila de beneficios. La etiqueta va
 * en tono atenuado sobre el valor para que la vista caiga en el dato real
 * (fecha, modalidad, certificacion).
 */
function BannerFeature({ icon, label, value }: BannerFeatureProps) {
  return (
    <div className="flex items-center gap-2">
      <Icon
        name={icon}
        size={20}
        className="flex-shrink-0 text-[var(--color-empowering)]"
      />
      <div className="flex flex-col leading-tight">
        <span className="text-[11px] text-[var(--color-on-surface-variant)]">
          {label}
        </span>
        <span className="text-[12px] font-bold text-[var(--color-empowering)]">
          {value}
        </span>
      </div>
    </div>
  );
}
